import { inject, Injectable, signal } from '@angular/core'
import { Observable, throwError } from 'rxjs'
import { switchMap } from 'rxjs/operators'
import { environment } from '../../../environments/environment'
import { OfferService } from '../../offer/shared/offer.service'
import { ValidationService } from '../validation/validation.service'
import {
  counterpartySchema,
  parkSchema,
  remoteControllabilitySchema,
  settlementSchema,
  productConfigurationSchema,
  PersonType,
  TechnicianContactSource,
  technicalResourcesSchema,
  type Product,
  type ProductionTechnology,
  ENBW_BDEW_CODE,
  OfferEntity,
} from '@onedv-portal/shared'
import { z } from 'zod'

const SESSION_KEY = 'onedv-dev-mode'
const URL_PARAM = 'dev'

type ParkData = z.infer<typeof parkSchema>
type TechnicalResourcesData = z.infer<typeof technicalResourcesSchema>
type CounterpartyData = z.infer<typeof counterpartySchema>
type SmallDvCounterpartyData = CounterpartyData & {
  bankAccount: SettlementData['bankAccount']
  invoiceRecipient: SettlementData['invoiceRecipient']
  additionalData: SettlementData['additionalData']
}
type RemoteControllabilityData = z.infer<typeof remoteControllabilitySchema>
type SettlementData = z.infer<typeof settlementSchema>
type ProductConfigurationData = z.infer<typeof productConfigurationSchema>

@Injectable({ providedIn: 'root' })
export class DevSettingsService {
  private readonly _isDevMode = signal(false)
  readonly isDevMode = this._isDevMode.asReadonly()

  private readonly offerService = inject(OfferService)
  private readonly validationService = inject(ValidationService)

  constructor() {
    if (environment.production) {
      sessionStorage.removeItem(SESSION_KEY)
      return
    }

    const params = new URLSearchParams(globalThis.location.search)
    if (params.has(URL_PARAM)) {
      const value = params.get(URL_PARAM)
      if (value === 'false' || value === '0') {
        sessionStorage.removeItem(SESSION_KEY)
      } else {
        sessionStorage.setItem(SESSION_KEY, 'true')
      }
    }

    this._isDevMode.set(sessionStorage.getItem(SESSION_KEY) === 'true')
  }

  createFullOffer(productionTechnology: ProductionTechnology, product: Product): Observable<OfferEntity> {
    if (environment.production || !this._isDevMode()) {
      return throwError(() => new Error('Dev mode is disabled.'))
    }

    return this.offerService
      .createOffer(productionTechnology, product)
      .pipe(
        switchMap((offer) =>
          this.prefill[product](offer.contractNumber, productionTechnology).pipe(
            switchMap(() => this.offerService.getOffer(offer.contractNumber))
          )
        )
      )
  }

  // which sections an offer has is its product's answer, so the fixtures are too; the record makes a new
  // product fail to compile until it brings its own
  private readonly prefill: Record<
    Product,
    (contractNumber: string, productionTechnology: ProductionTechnology) => Observable<unknown>
  > = {
    largeDV: (contractNumber, productionTechnology) => this.patchAllSections(contractNumber, productionTechnology),
    smallDV: (contractNumber, productionTechnology) => this.patchSmallDvSections(contractNumber, productionTechnology),
  }

  private patchSmallDvSections(
    contractNumber: string,
    productionTechnology: ProductionTechnology
  ): Observable<unknown> {
    return this.offerService
      .patchOffer(contractNumber, 'park', DevSettingsService.buildSmallDvTechnicalDetailsData(productionTechnology))
      .pipe(
        switchMap(() =>
          this.offerService.patchOffer(
            contractNumber,
            'counterparty',
            DevSettingsService.buildSmallDvCounterpartyData(productionTechnology)
          )
        ),
        switchMap(() =>
          this.offerService.patchOffer(
            contractNumber,
            'remoteControllability',
            DevSettingsService.buildRemoteControllabilityData()
          )
        )
      )
  }

  private patchAllSections(contractNumber: string, productionTechnology: ProductionTechnology): Observable<unknown> {
    return this.offerService
      .patchOffer(contractNumber, 'park', DevSettingsService.buildParkData(productionTechnology))
      .pipe(
        switchMap(() =>
          this.offerService.patchOffer(
            contractNumber,
            'technicalresources',
            DevSettingsService.buildTechnicalResourcesData(productionTechnology)
          )
        ),
        switchMap(() =>
          this.offerService.patchOffer(
            contractNumber,
            'counterparty',
            DevSettingsService.buildCounterpartyData(productionTechnology)
          )
        ),
        switchMap(() => this.patchSettlementWithValidatedIban(contractNumber)),
        switchMap(() =>
          this.offerService.patchOffer(
            contractNumber,
            'productconfiguration',
            DevSettingsService.buildProductConfigData()
          )
        )
      )
  }

  private patchSettlementWithValidatedIban(contractNumber: string): Observable<unknown> {
    const settlement = DevSettingsService.buildSettlementData()
    return this.validationService.validateIban(settlement.bankAccount.iban).pipe(
      switchMap((result) => {
        settlement.bankAccount.bic = result.bic ?? settlement.bankAccount.bic
        settlement.bankAccount.bank = result.name ?? settlement.bankAccount.bank
        return this.offerService.patchOffer(contractNumber, 'settlement', settlement)
      })
    )
  }

  public static buildParkData(productionTechnology: ProductionTechnology): ParkData {
    return {
      park: {
        locationNameCustomer: `Dev ${productionTechnology} Park`,
        productionTechnology,
        isAlreadyInOperation: true,
        controlArea: '10YDE-ENBW-----N',
      },
      meteringPoint: {
        connectionServiceProvider: 'Unbekannt',
      },
      marketLocations: [
        {
          projectIdGridOperator: 'PRJ-2024-00742',
          gridOperator: '9900687000004',
          voltageLevel: 'Mittelspannung',
          addressStreetOrPOBox: 'Musterstraße',
          addressPostalCode: '70173',
          addressCity: 'Stuttgart',
          previousMarketingType: 'Geförderte Direktvermarktung',
        },
      ],
    }
  }

  private static smallDvContractStart(): Date {
    const earliest = new Date(Date.now() + 6 * 7 * 24 * 60 * 60 * 1000)
    return new Date(Date.UTC(earliest.getUTCFullYear(), earliest.getUTCMonth() + 1, 1))
  }

  private static buildSmallDvTechnicalDetailsData(productionTechnology: ProductionTechnology) {
    const contractStart = DevSettingsService.smallDvContractStart()
    return {
      locationNameCustomer: `Dev ${productionTechnology} Park`,
      generalContractTerms: {
        productionTechnology,
        isAlreadyInOperation: false,
        directMarketingRequired: 'Freiwillige Direktvermarktung (gefördert)',
        contractStart,
        contractEnd: new Date(Date.UTC(contractStart.getUTCFullYear() + 1, contractStart.getUTCMonth(), 1)),
      },
      identification: {
        marketLocationId: 99000000001,
        isMaLoSplitIntoMultipleMaLos: false,
        previousDirectMarketer: '9979694000000',
        gridOperator: '9900687000004',
        controlArea: '10YDE-ENBW-----N',
        voltageLevel: 'Mittelspannung',
      },
      generation: {
        totalInstalledCapacity: 7_000,
        plannedProductionPerYear: 2_000,
        ownConsumptionPercent: 10,
        ownConsumptionPerYear: 200,
      },
      technicalResources: [
        {
          technicalData: {
            installedCapacity: 4000,
            bifacial: false,
            tracking: false,
            tiltFrom: 35,
            orientation: 'South',
          },
          technicalResourceIdentification: {
            mastrNumber: 'SEE123456789012',
            eegPlantKey: 'E12345678901234567890123456789012',
            trId: 'D1234567892',
            srId: 'C1234567893',
          },
          marketing: {
            commissioningDatePlanned: contractStart,
            applicableValueCentsPerkWh: 80,
          },
          coordinates: {
            latitude: '48.500',
            longitude: '9.000',
          },
        },
        {
          technicalData: {
            installedCapacity: 3000,
            bifacial: false,
            tracking: false,
            tiltFrom: 20,
            orientation: 'South',
          },
          technicalResourceIdentification: {
            mastrNumber: 'SEE210987654321',
            eegPlantKey: 'E21098765432109876543210987654321',
            trId: 'D2109876540',
            srId: 'C2109876541',
          },
          marketing: {
            commissioningDatePlanned: contractStart,
            applicableValueCentsPerkWh: 75,
          },
          coordinates: {
            latitude: '48.510',
            longitude: '9.010',
          },
        },
      ],
      location: {
        streetOrPoBox: 'Musterstraße',
        houseNumber: '1',
        postalCode: '70173',
        city: 'Stuttgart',
      },
    }
  }

  private static buildTechnicalResourcesData(productionTechnology: ProductionTechnology): TechnicalResourcesData {
    if (productionTechnology === 'Solar') {
      return {
        solar: [
          {
            projectId: 'PRJ-2024-00742',
            installedCapacity: 1,
            directMarketingRequired: 'Freiwillige Direktvermarktung (gefördert)',
            commissioningDatePlanned: new Date(new Date().setFullYear(2099)),
            latitude: '48.500',
            longitude: '9.000',
            photovoltaic: {
              elevationAboveSeaLevel: 200,
              tracking: true,
              bifacial: false,
            },
          },
        ],
        wind: null,
      }
    }

    return {
      solar: null,
      wind: [
        {
          projectId: 'PRJ-2024-00742',
          installedCapacity: 2,
          directMarketingRequired: 'Freiwillige Direktvermarktung (gefördert)',
          commissioningDate: new Date(new Date().setFullYear(2025)),
          redispatchTechnicalResource: { rdTrId: 'D1234567892', rdSrId: 'C1234567893' },
          latitude: '52.506',
          longitude: '13.000',
          windTurbine: {
            manufacturer: 'Enercon',
            turbineType: 'E82/2300',
            serialNumber: 'DE-00001',
            hubHeight: 80,
          },
        },
      ],
    }
  }

  private static buildCounterpartyData(productionTechnology: ProductionTechnology): CounterpartyData {
    const contactPersonEnbwSchema: CounterpartyData['contactPersonEnbw'] = {
      lastName: 'Tester',
      firstName: 'Dev',
      communication: {
        phone: '0711000000',
        email: 'dev.tester@enbw.com',
      },
    }
    const remit: CounterpartyData['remit'] = { remitObligation: false }

    if (productionTechnology === 'Solar') {
      return {
        personType: PersonType.COMPANY,
        company: {
          companyName: 'Dev Test GmbH',
          registrationType: 'HRB',
          registrationNumber: '123456',
          registrationAuthorityName: 'Stuttgart',
        },
        address: {
          streetOrPoBox: 'Musterstraße',
          houseNumber: '1',
          postalCode: '70173',
          city: 'Stuttgart',
          country: 'DEU',
        },
        contactPersonCounterparty: {
          lastName: 'Mustermann',
          firstName: 'Erika',
          communication: { email: 'erika.mustermann@example.de', phone: '0711000002' },
        },
        contactPersonEnbw: contactPersonEnbwSchema,
        remit,
      }
    }

    return {
      personType: PersonType.PERSON,
      person: { firstName: 'Max', lastName: 'Mustermann' },
      address: {
        streetOrPoBox: 'Windstraße',
        houseNumber: '5',
        postalCode: '10115',
        city: 'Berlin',
        country: 'DEU',
      },
      contactPersonCounterparty: {
        lastName: 'Mustermann',
        firstName: 'Max',
        communication: { email: 'max.mustermann@example.de', phone: '0300000002' },
      },
      contactPersonEnbw: contactPersonEnbwSchema,
      remit,
    }
  }

  private static buildSmallDvCounterpartyData(productionTechnology: ProductionTechnology): SmallDvCounterpartyData {
    const counterparty = DevSettingsService.buildCounterpartyData(productionTechnology)
    const settlement = DevSettingsService.buildSettlementData()

    return {
      ...counterparty,
      bankAccount: settlement.bankAccount,
      invoiceRecipient: settlement.invoiceRecipient,
      additionalData: settlement.additionalData,
    }
  }

  private static buildRemoteControllabilityData(): RemoteControllabilityData {
    return {
      existingThirdPartyHardware: false,
      serviceProvider: 'amperecloud',
      technicianContactSource: TechnicianContactSource.OTHER,
      technician: {
        firstName: 'Dev',
        lastName: 'Technician',
        communication: {
          phone: '0711000001',
          email: 'dev.technician@enbw.com',
        },
      },
    }
  }

  private static buildSettlementData(): SettlementData {
    return {
      bankAccount: {
        accountHolder: 'Dev Test GmbH',
        iban: 'DE89370400440532013000',
        bic: '',
        bank: '',
      },
      invoiceRecipient: {
        email1: 'rechnung@dev-test.de',
      },
      billingGroup: {
        splittingMethod: 'Keine',
      },
      additionalData: {
        vatId: 'DE123456789',
        arPartnerType: 'Endverbraucher',
      },
    }
  }

  private static buildProductConfigData(): ProductConfigurationData {
    return {
      contract: {
        contractStart: new Date(Date.now() + 1 * 24 * 60 * 60 * 1000),
        contractEnd: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
      },
      redispatch: {
        balancingModel: 'Prognosemodell',
        callProcedure: 'Aufforderungsfall',
        billingModel: 'Kein Abrechnungsmodell',
        roleBTR: 'EnBW',
        operatorBTR: ENBW_BDEW_CODE,
      },
      product: {
        productType: 'Monatsmarktwert (geförderte DV)',
      },
      productConfiguration1: {
        feedIn: {
          sharePercentage: 100,
          regime: 'MW',
          DLEDeduction: true,
        },
        DVEntgelt: {
          DLEModel: 'Fixes DLE',
          DLEFix: 5,
        },
        compensation: {
          compensationType: 'Anzulegender Wert',
          DLEDeduction: true,
          curtailmentEnergyCalculation: 'Kein Abrechnungsmodell',
        },
        deviatingCompensationAtP51: {
          deviatingCompensationType: 'Monatsmarktwert',
          DLEDeduction: true,
        },
      },
    }
  }

  public static buildFullOfferData(productionTechnology: ProductionTechnology = 'Solar') {
    return {
      park: DevSettingsService.buildParkData(productionTechnology),
      technicalresources: DevSettingsService.buildTechnicalResourcesData(productionTechnology),
      counterparty: DevSettingsService.buildCounterpartyData(productionTechnology),
      settlement: DevSettingsService.buildSettlementData(),
      productconfiguration: DevSettingsService.buildProductConfigData(),
    }
  }
}
